import React, { useEffect } from 'react';
import { IssueDetails } from "~/types";
import classes from './style.module.scss';
import useTranslation from "~/app/hooks/useTranslation";
import Icon from "~/app/common/Icon";
import { useStoreActions, useStoreState } from '~/store';
import { useSelectedIssueSortingType } from '~/app/data/statuses/issue';
import { getContrastYIQ, getLabelsMap } from '~/issues/helpers';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { isIssueAwaitingSync } from '~/data/sync/issueQueue';
import { usePendingIssues } from '~/issues/usePendingIssues';

export interface IssueProps {
    issue: IssueDetails
}

export type IssueComponent = React.FC<IssueProps>

const Issue: IssueComponent = ({issue}) => {
    const {t} = useTranslation("issue");
    const {t: tOffline} = useTranslation("offline");

    const fetchingLabels = useStoreState(state => state.issue.fetchingLabels);
    const fetchLabels = useStoreActions(actions => actions.issue.fetchLabels);
    const labels = useStoreState(state => state.issue.labels);
    const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
    const sortingTypes = useStoreState(state => state.issue.sortingTypes);
    const selectedSortingType = useSelectedIssueSortingType(sortingTypes, issue?.sortingType as string | number);
    const { pending, loaded } = usePendingIssues([{ id: issue.id, guid: issue.guid }]);
    const awaitingSync = isIssueAwaitingSync(pending, loaded, issue.id, issue.guid, issue.pendingSync);

    useEffect(() => {
        if (labels.length === 0) {
            fetchLabels();
        }
    }, [fetchLabels, labels.length]);

    if (fetchingLabels) {
        return <></>;
    }

    return (
        <div className={classes['issue-info']}>
            <div className={classes['issue-title']}>{issue.description}</div>
            <div className={classes['issue-register-details']}>{t('registerTimeName', {
                date: issue.registrationDate,
                name: issue.createdBy
            })}</div>
            <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("statusTitle")}</div>
                <div className={`${classes['value']} ${classes['status-value']}`}>
                    <Icon name="stop_circle" className={`${classes['status']} ${classes[issue.status]}`}/>
                    {t(`statuses.${issue.status}`)}
                </div>
                {awaitingSync && <div className={classes['awaiting-sync']}>{tOffline('awaitingSynchronization')}</div>}
            </div>
            <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("dueDateTitle")}</div>
                <div className={classes['value']}>{t('dueDate', {date: issue.dueDate})}</div>
            </div>
            <AccessControl requiredPermissions={PERMISSION_GROUPS.viewIssueTag}>
                <div className={classes['labels-wrapper']}>
                    {issue.labels && labels && issue.labels.split(",")
                        .map(label => getLabelsMap(labels).get(parseInt(label)))
                        .filter(issueLabel => issueLabel && issueLabel?.id && issueLabel.id !== 1)
                        .map((issueLabel, index) => (
                            <span
                                key={index}
                                className={classes['issue-label']}
                                style={{
                                    background: issueLabel?.colorName,
                                    color: getContrastYIQ(issueLabel?.colorName),
                                }}
                            >
                                {issueLabel?.name}
                            </span>
                        ))}
                </div>
            </AccessControl>
            <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("classification.title")}</div>
                <div className={classes['value']}>{t(`types.${issue.type}`)}</div>
            </div>
            <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("issueCategories")}</div>
                <div className={classes['value']}>{issue.categoryName}</div>
            </div>
            {issue.issueDepartmentId && <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("issueDepartment")}</div>
                <div className={classes['value']}>{issue.issueDepartmentName}</div>
            </div>}
            {!!displayTypeInIssues && selectedSortingType?.length > 0 && <div className={classes['info-wrapper']}>
                <div className={classes['title']}>{t("sortingType.title")}</div>
                <div className={classes['value']}>{selectedSortingType}</div>
            </div>}
            <hr className={classes['bottom-line']}/>
        </div>
    );
};

export default Issue
